import { cn } from "@app/ui";
import { useI18n } from "../../../../lib/useI18n.js";
import { useCalendarsStore, useLabelsStore, useThemeStore } from "../../../../stores/index.js";
import { resolvedCalendarColor } from "../../../entity-colors.js";
import { useHorizontalDragScroll } from "./useHorizontalDragScroll.js";

interface Props {
  calendarId: string | null;
  setCalendarId: (id: string) => void;
  labelIds: string[];
  setLabelIds: (ids: string[]) => void;
  tz: string | null;
  setTz: (tz: string | null) => void;
}

function tzOptions(): string[] {
  try {
    const all = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.("timeZone");
    if (all && all.length) return all;
  } catch { /* not supported */ }
  return [Intl.DateTimeFormat().resolvedOptions().timeZone];
}

function readable(color: string): string {
  const h = /^#?([0-9a-f]{6})$/i.exec(color.trim());
  if (!h) return "#1f2937";
  const n = parseInt(h[1]!, 16);
  const lum = 0.299 * ((n >> 16) & 0xff) + 0.587 * ((n >> 8) & 0xff) + 0.114 * (n & 0xff);
  return lum > 150 ? "#1f2937" : "#f8fafc";
}

/** Bottom mobile rails for calendar and label metadata. */
export function AdvancedFields(p: Props) {
  const { t } = useI18n();
  const calendars = useCalendarsStore((s) => s.calendars);
  const labels = useLabelsStore((s) => s.labels);
  const theme = useThemeStore((s) => s.config);
  const calendarScroll = useHorizontalDragScroll();
  const labelScroll = useHorizontalDragScroll();
  const { fadeStyle: calendarFade, ...calendarScrollProps } = calendarScroll;
  const { fadeStyle: labelFade, ...labelScrollProps } = labelScroll;

  return (
    <div className="flex flex-col gap-2 px-1">
      {calendars.length > 0 && (
        <div
          className="no-scrollbar -mx-4 flex cursor-grab flex-nowrap gap-1.5 overflow-x-auto px-4 pb-1 active:cursor-grabbing"
          style={{ touchAction: "none", scrollbarWidth: "none", ...calendarFade }}
          {...calendarScrollProps}
        >
          {calendars.map((c) => {
            const on = p.calendarId === c.id;
            const col = resolvedCalendarColor(theme, calendars, c.id) ?? "#94a3b8";
            return (
              <button
                key={c.id}
                type="button"
                className={cn("flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-sm transition-colors", on ? "font-medium" : "bg-background")}
                style={on ? { background: col, borderColor: col, color: readable(col) } : { borderColor: col }}
                onClick={() => p.setCalendarId(c.id)}
              >
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: on ? "currentColor" : col }} />
                {c.name}
              </button>
            );
          })}
        </div>
      )}

      {labels.length > 0 && (
        <div
          className="no-scrollbar -mx-4 flex cursor-grab flex-nowrap gap-1.5 overflow-x-auto px-4 pb-1 active:cursor-grabbing"
          style={{ touchAction: "none", scrollbarWidth: "none", ...labelFade }}
          {...labelScrollProps}
        >
          {labels.map((l) => {
            const on = p.labelIds.includes(l.id);
            const col = l.color ?? "#94a3b8";
            return (
              <button
                key={l.id}
                type="button"
                className={cn("flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-sm transition-colors", on ? "font-medium" : "bg-background")}
                style={on ? { background: col, borderColor: col, color: readable(col) } : { borderColor: col }}
                onClick={() => p.setLabelIds(on ? p.labelIds.filter((id) => id !== l.id) : [...p.labelIds, l.id])}
              >
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: on ? "currentColor" : col }} />
                {l.name}
              </button>
            );
          })}
        </div>
      )}

      <select
        value={p.tz ?? ""}
        onChange={(e) => p.setTz(e.target.value || null)}
        className="w-full rounded-md border bg-background px-3 py-2 text-sm"
        aria-label={t("taskForm.timeZone")}
      >
        <option value="">{t("taskForm.deviceDefault")}</option>
        {tzOptions().map((z) => <option key={z} value={z}>{z}</option>)}
      </select>
    </div>
  );
}
